import {Grid, SectionIntro, Pillar, Box} from '@primer/react-brand/esm'
import {getImageSources, MAX_CONTENT_WIDTH} from '@github-ui/swp-core/lib/utils/images'
import type {ImageLinkItem} from '../../types'
import {getAssetImageStyle, renderWithoutParagraphs} from '../../utils'
import type {Document} from '@contentful/rich-text-types'
import styles from './FlexSuiteRiverFootsteps.module.css'
import baseStyles from '../../styles/base.module.css'

export type FlexSuiteRiverFootstepsProps = {
  heading?: Document
  label?: string
  cta?: {href: string; text: string}
  cards?: ImageLinkItem[]
}

// This custom implementation will be replaced with a new Primer Brand Component, when available.
export function FlexSuiteRiverFootsteps({heading, label, cta, cards}: FlexSuiteRiverFootstepsProps) {
  const riverCards = cards?.map(c => {
    const id = c.sys.id
    const card = c.fields
    const imageUrl = card.image?.fields.file.url
    const imageAlt = card.image?.fields.description || ''
    const imageStyle = getAssetImageStyle(card.image)
    if (!imageUrl) return null
    return (
      <Grid.Column span={{small: 12, medium: 6}} key={id}>
        <Pillar fullWidth>
          <Pillar.Image
            as="picture"
            aspectRatio={imageStyle ? 'custom' : undefined}
            style={imageStyle}
            src={`${imageUrl}?fm=webp`}
            alt={imageAlt}
            sources={getImageSources(imageUrl, {maxWidth: MAX_CONTENT_WIDTH / 2})}
            loading="lazy"
            className={styles.riverFootstepsImage}
          />
          {card.heading && <Pillar.Heading>{renderWithoutParagraphs(card.heading)}</Pillar.Heading>}
          {card.description && <Pillar.Description>{renderWithoutParagraphs(card.description)}</Pillar.Description>}
          {card.href && (
            <Pillar.Link href={card.href}>
              {card.label}
              {card.alt ? <span className="sr-only"> {card.alt}</span> : null}
            </Pillar.Link>
          )}
        </Pillar>
      </Grid.Column>
    )
  })
  return (
    <Box
      className={`${baseStyles.gridFrameTop} ${baseStyles.gridFrameBottom}`}
      paddingBlockStart={{narrow: 'spacious', regular: 64}}
      paddingBlockEnd={{narrow: 'spacious', regular: 64}}
    >
      <Grid columnGap="none" rowGap="none" enableGutters={false}>
        <Grid.Column span={{xsmall: 12, large: 5}}>
          <SectionIntro>
            {label && <SectionIntro.Label>{label}</SectionIntro.Label>}
            {heading && <SectionIntro.Heading as="h3">{renderWithoutParagraphs(heading)}</SectionIntro.Heading>}
            {cta && <SectionIntro.Link href={cta.href}>{cta.text}</SectionIntro.Link>}
          </SectionIntro>
        </Grid.Column>
        <Grid.Column span={{xsmall: 12, large: 7}}>
          <Grid className={styles.riverFootstepsGrid}>{riverCards}</Grid>
        </Grid.Column>
      </Grid>
    </Box>
  )
}
